import {type FetchResult, ServerError} from '@yandex-delivery/core/utils/network'
import {delay, equals, findLast, flow, get} from '@yandex-delivery/utils'
import {config} from 'src/shared/config'
import type {ApiMode} from 'src/shared/types'

import webStateAuthMock from './__mocks__/web-state-auth'
import webStateAuthFeedbackMock from './__mocks__/web-state-auth-feedback'
import {webStatePostpayMock, webStatePostpayWithButtonMock} from './__mocks__/web-state-postpay'
import {isSameDeliveryId} from './deliveriesEvents'
import {toFeedbackRequestBody, toFeedbackRequestBodyAuth, toPollRequestBodyAuth, webStateToSharedRoute} from './mappers'
import type * as t from './types'
import type {WebTrackingResponse} from './types/webState'
import {yaAuthFetch} from './ya-auth-fetch'

const NIL = '00000000-0000-0000-0000-000000000000'
const getBody = get('body')

type ApiEndpoints = {
  basePath: string
  endpoints: Record<string, string>
}

const API_CONFIG: Record<ApiMode, ApiEndpoints> = {
  default: {
    basePath: '/4.0/cargo-c2c/v1/shared-route',
    endpoints: {},
  },
  auth: {
    basePath: '/4.0/cargo-c2c/v1/web',
    endpoints: {
      '/info': '/state',
      '/events': '/deliveries/events',
    },
  },
}

const getApiConfig = () => API_CONFIG[config.apiMode ?? 'default']

const withCargoC2CAPIPath = (endpoint: string) => {
  const apiConfig = getApiConfig()
  const mappedEndpoint = apiConfig.endpoints[endpoint] ?? endpoint
  return `${apiConfig.basePath}${mappedEndpoint}`
}

const DEBUG_MOCKS = {
  delivering: webStateAuthMock,
  feedback: webStateAuthFeedbackMock,
  postpay: webStatePostpayMock,
  postpayButton: webStatePostpayWithButtonMock,
} as const

type DebugMockVariant = keyof typeof DEBUG_MOCKS

const allowDebugMocks = process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ALLOW_DEBUG_MOCKS === 'true'

const getDebugMockVariant = (): DebugMockVariant | null => {
  if (typeof window === 'undefined') return null
  const debug = new URLSearchParams(window.location.search).get('debug')
  if (debug === '1') return 'delivering'
  if (debug === '2') return 'feedback'
  if (debug === '3') return 'postpay'
  if (debug === '4') return 'postpayButton'
  return null
}

export const fetchSharedRouteInfo = async (body: t.SharedRouteKey): Promise<FetchResult<t.SharedRouteInfoResponse>> => {
  if (allowDebugMocks) {
    if (body.key === NIL) {
      // biome-ignore lint/suspicious/noConsole: отладочный вывод в режиме debug-моков
      console.log('[fetchSharedRouteInfo] using NIL devMocks')
      const {loadSharingMock} = await import('./devMocks')
      return loadSharingMock()
    }

    const debugVariant = getDebugMockVariant()
    if (debugVariant) {
      // biome-ignore lint/suspicious/noConsole: отладочный вывод в режиме debug-моков
      console.log(`[fetchSharedRouteInfo] using debug mock: ${debugVariant}`)
      const mockResponse: WebTrackingResponse = DEBUG_MOCKS[debugVariant]
      return {
        body: webStateToSharedRoute(mockResponse.state),
        headers: {'X-Refresh-After': '5000'},
      }
    }
  }

  const apiMode = config.apiMode
  const requestBody = apiMode === 'auth' ? {delivery_id: body.key} : body
  const requestUrl = withCargoC2CAPIPath('/info')

  const result = await yaAuthFetch<t.SharedRouteInfoResponse | WebTrackingResponse>({
    url: requestUrl,
    body: requestBody,
    method: 'POST',
  })

  if (apiMode === 'auth' && typeof result.body === 'object' && result.body !== null && 'state' in result.body) {
    const webTrackingResponse = result.body as WebTrackingResponse
    return {
      ...result,
      body: webStateToSharedRoute(webTrackingResponse.state),
    }
  }

  return result as FetchResult<t.SharedRouteInfoResponse>
}

export const fetchPerformerPosition = async (
  body: t.SharedRouteKey,
): Promise<t.PerformerPositionResponseWithDirection> => {
  if (allowDebugMocks) {
    if (getDebugMockVariant() || body.key === NIL) {
      const {loadPerformerMock} = await import('./devMocks')
      return loadPerformerMock().then(getBody)
    }
  }

  // В auth mode /web/performer-position принимает { delivery_id }
  const apiMode = config.apiMode
  const requestBody = apiMode === 'auth' ? {delivery_id: body.key} : body

  return yaAuthFetch<t.PerformerPositionResponseWithDirection>({
    url: withCargoC2CAPIPath('/performer-position'),
    body: requestBody,
    method: 'POST',
    retries: 0,
  }).then(getBody)
}

export const fetchPhoneByVoiceForwarding = (body: t.SharedRouteVoiceforwardingRequest): Promise<t.Voiceforwarding> =>
  yaAuthFetch<t.Voiceforwarding>({
    url: withCargoC2CAPIPath('/voiceforwarding'),
    body,
    method: 'POST',
  }).then(getBody)

// /web/voiceforwarding принимает VoiceforwardingRequest: { delivery_id, forwarding_id },
// additionalProperties: false — поле key передавать нельзя
export const fetchPhoneByVoiceForwardingAuth = (body: t.VoiceforwardingRequest): Promise<t.Voiceforwarding> =>
  yaAuthFetch<t.Voiceforwarding>({
    url: withCargoC2CAPIPath('/voiceforwarding'),
    body,
    method: 'POST',
  }).then(getBody)

export const fetchSendFeedback = (userFeedback: t.UserFeedback, sharingKey: string): Promise<unknown> =>
  yaAuthFetch({
    url: withCargoC2CAPIPath('/feedback'),
    body:
      config.apiMode === 'auth'
        ? toFeedbackRequestBodyAuth(userFeedback, sharingKey)
        : toFeedbackRequestBody(userFeedback, sharingKey),
    method: 'POST',
    idempotent: true,
  })

export const fetchSharedRoutePoll = (sharingKey: string, answersChoices: t.QuestionChoice[]): Promise<unknown> =>
  yaAuthFetch({
    url: withCargoC2CAPIPath('/poll'),
    body:
      config.apiMode === 'auth'
        ? toPollRequestBodyAuth(sharingKey, answersChoices)
        : {key: sharingKey, answers_choices: answersChoices},
    method: 'POST',
    idempotent: true,
  })

function fetchCancel(body: t.SharedRouteCancelRequest): Promise<t.OperationIdResponse> {
  // return import('./__mocks__/shared-route-cancel-paid/cancel').then(r => r.default(body))
  return yaAuthFetch<t.OperationIdResponse>({
    url: withCargoC2CAPIPath('/cancel'),
    body,
    method: 'post',
    idempotent: true,
  }).then(getBody)
}

type FetchCancelAuthParams = {
  key: string
  cancel_type: t.CancelType
  request_id: string
}

function fetchCancelAuth(params: FetchCancelAuthParams): Promise<t.OperationIdResponse> {
  const {key, cancel_type, request_id} = params

  // /web/cancel принимает DeliveryCancelRequest: { delivery_id, cancel_type, request_id }
  const requestBody = {
    delivery_id: key,
    cancel_type,
    request_id,
  }

  return yaAuthFetch<t.OperationIdResponse>({
    url: withCargoC2CAPIPath('/cancel'),
    body: requestBody,
    method: 'post',
    idempotent: true,
  }).then(getBody)
}

function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // Fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function fetchEvents(body: t.SharedRouteEventsRequest): Promise<t.SharedRouteEventsResponse> {
  // return import('./__mocks__/shared-route-cancel-paid/events').then(r => r.default(body))
  return yaAuthFetch<t.SharedRouteEventsResponse>({
    url: withCargoC2CAPIPath('/events'),
    body,
    method: 'post',
  }).then(getBody)
}

type FetchEventsAuthParams = {
  key: string
  revision?: string
}

export function fetchEventsAuth(params: FetchEventsAuthParams): Promise<t.SharedRouteEventsAuthResponse> {
  const {key, revision} = params

  // /web/deliveries/events принимает только revision?: string, без key
  // События фильтруем на клиенте по delivery_id
  // Если revision передан не как string (edge case), сериализуем в string
  const normalizedRevision = revision ? (typeof revision === 'string' ? revision : JSON.stringify(revision)) : undefined

  const requestBody = normalizedRevision ? {revision: normalizedRevision} : {}

  return yaAuthFetch<t.SharedRouteEventsAuthResponse>({
    url: withCargoC2CAPIPath('/events'),
    body: requestBody,
    method: 'post',
  }).then(response => {
    const body = getBody(response) as t.SharedRouteEventsAuthResponse

    // Извлекаем X-Refresh-After header
    const refreshAfterMs = response.headers?.['x-refresh-after']
      ? Number(response.headers['x-refresh-after'])
      : undefined

    // Фильтруем события по delivery_id через helper isSameDeliveryId
    const filteredEvents = body.events.filter(event => {
      if (!('delivery_id' in event)) return false

      const eventDeliveryId = event.delivery_id as string
      return isSameDeliveryId(eventDeliveryId, key)
    })

    // Нормализуем revision в string (на случай если backend вернул не-string)
    const normalizedResponseRevision = typeof body.revision === 'string' ? body.revision : JSON.stringify(body.revision)

    return {
      ...body,
      revision: normalizedResponseRevision,
      events: filteredEvents,
      refreshAfterMs,
    }
  })
}

const getCancelSucceeded: (response: t.SharedRouteEventsResponse) => t.OrderCancelSucceeded | undefined = flow(
  get('events'),
  findLast<t.OrderCancelSucceeded>(flow(get('type'), equals('order-cancel-succeeded'))),
)
const getCancelFailed: (response: t.SharedRouteEventsResponse) => t.OrderCancelFailed | undefined = flow(
  get('events'),
  findLast<t.OrderCancelFailed>(flow(get('type'), equals('order-cancel-failed'))),
)

export function fetchSharedRouteCancel(body: t.CancelBody): Promise<void> {
  const {key} = body

  async function fetchEventsPolling(revision: number): Promise<void> {
    const response = await fetchEvents({key, revision})
    const succeeded = getCancelSucceeded(response)
    const failed = getCancelFailed(response)

    if (succeeded) {
      return
    }

    if (failed && failed.message) {
      throw new ServerError<t.CancelPaidError>({
        body: {
          message: failed.message,
          revision: response.revision,
        },
        status: 409,
        // @ts-expect-error кривая типизация у ошибки
        error: new Error(failed.message.title),
      })
    }

    return delay(1000).then(() => fetchEventsPolling(response.revision))
  }

  return fetchCancel({key, cancel_type: body.type})
    .then(() => delay(1000))
    .then(() => fetchEventsPolling(body.revision))
}

const MAX_POLLING_ATTEMPTS = 60 // 60 секунд при delay 1000ms

export function fetchSharedRouteCancelAuth(body: t.CancelBody): Promise<void> {
  const {key} = body
  const requestId = generateRequestId()

  async function fetchEventsPollingAuth(revision?: string, attempt = 0): Promise<void> {
    if (attempt >= MAX_POLLING_ATTEMPTS) {
      console.warn(`[fetchSharedRouteCancelAuth] polling timeout after ${MAX_POLLING_ATTEMPTS} attempts`)
      // Возвращаемся без ошибки - операция может завершиться позже
      return
    }

    const response = await fetchEventsAuth({key, revision})

    // Фильтруем события по request_id (дополнительно к фильтрации по delivery_id внутри fetchEventsAuth)
    const succeeded = response.events.find(
      event => event.type === 'order-cancel-succeeded' && 'request_id' in event && event.request_id === requestId,
    ) as t.OrderCancelSucceeded | undefined

    const failed = response.events.find(
      event => event.type === 'order-cancel-failed' && 'request_id' in event && event.request_id === requestId,
    ) as t.OrderCancelFailed | undefined

    if (succeeded) {
      return
    }

    if (failed && failed.message) {
      throw new ServerError<t.CancelPaidError>({
        body: {
          message: failed.message,
          revision: 0, // В auth mode revision - string, но Message.revision ожидает number
        },
        status: 409,
        // @ts-expect-error кривая типизация у ошибки
        error: new Error(failed.message.title),
      })
    }

    return delay(1000).then(() => fetchEventsPollingAuth(response.revision, attempt + 1))
  }

  return fetchCancelAuth({key, cancel_type: body.type, request_id: requestId})
    .then(() => delay(1000))
    .then(() => fetchEventsPollingAuth())
}

export function fetchPromotions(body: t.PromotionsRequest): Promise<FetchResult<t.PromotionsResponse>> {
  // return import('./__mocks__/delivery-promotions').then(r => r.default)

  return yaAuthFetch<t.PromotionsResponse>({
    url: withCargoC2CAPIPath('/v1/delivery-promotions'),
    method: 'post',
    body,
  })
}

const fetchOpenRover = (body: t.DeliveryRoverActionRequest): Promise<t.OperationIdResponse> => {
  return yaAuthFetch<t.OperationIdResponse>({
    url: withCargoC2CAPIPath('/rover-action'),
    body,
    method: 'POST',
  }).then(get('body'))
}

const SUCCEEDED_TYPE = 'rover-action-succeeded'
const FAILED_TYPE = 'rover-action-failed'

const getRoverActionSucceeded: (response: t.SharedRouteEventsResponse) => t.RoverActionSucceeded | undefined = flow(
  get('events'),
  findLast<t.RoverActionSucceeded>(flow(get('type'), equals(SUCCEEDED_TYPE))),
)

const getRoverActionFailed: (response: t.SharedRouteEventsResponse) => t.RoverActionFailed | undefined = flow(
  get('events'),
  findLast<t.RoverActionFailed>(flow(get('type'), equals(FAILED_TYPE))),
)

export function fetchOpenRoverAndEventPolling(
  body: t.DeliveryRoverActionRequest,
  revision: number,
  onSendOpenCommand?: VoidFunction,
): Promise<t.RoverEventPollingResponse> {
  const {key, action, request_id: requestId} = body

  async function fetchEventsPolling(revision: number): Promise<t.RoverEventPollingResponse> {
    const response = await fetchEvents({key, revision})
    const succeeded = getRoverActionSucceeded(response)
    const failed = getRoverActionFailed(response)

    if (
      succeeded &&
      action === succeeded.action &&
      requestId === succeeded.request_id &&
      key === succeeded.delivery_id
    ) {
      return {revision: response.revision}
    }

    if (failed && action === failed.action && requestId === failed.request_id && key === failed.delivery_id) {
      return {
        revision: response.revision,
        message: failed,
      }
    }

    return delay(1000).then(() => fetchEventsPolling(response.revision))
  }

  return fetchOpenRover(body)
    .then(() => onSendOpenCommand?.())
    .then(() => delay(1000))
    .then(() => fetchEventsPolling(revision))
}

export const fetchEditOptions = async (deliveryId: string, requestId: string): Promise<t.DeliveryOptionsResponse> => {
  if (allowDebugMocks && getDebugMockVariant()) {
    // biome-ignore lint/suspicious/noConsole: отладочный вывод в режиме debug-моков
    console.log('[fetchEditOptions] using debug mock')
    const {default: editOptionsMock} = await import('./__mocks__/edit-options-storage-period')
    return editOptionsMock
  }

  const body: t.DeliveryOptionsRequest = {
    delivery_id: deliveryId,
    request_id: requestId,
    payload: {
      type: 'storage_period',
    },
  }

  return yaAuthFetch<t.DeliveryOptionsResponse>({
    url: withCargoC2CAPIPath('/edit-options'),
    body,
    method: 'POST',
  }).then(getBody)
}

export const fetchEdit = async (
  deliveryId: string,
  requestId: string,
  expirationDate: string,
): Promise<t.OperationIdResponse> => {
  const body: t.DeliveryEditRequest = {
    delivery_id: deliveryId,
    request_id: requestId,
    edit_options: null,
    edit_payload: {
      type: 'logistic-platform',
      edit_options: [
        {
          type: 'storage-period',
          expiration_date: expirationDate,
        },
      ],
    },
  }

  return yaAuthFetch<t.OperationIdResponse>({
    url: withCargoC2CAPIPath('/edit'),
    body,
    method: 'POST',
    idempotent: true,
  }).then(getBody)
}

export function fetchSharedRouteEditAuth(key: string, expirationDate: string): Promise<void> {
  const requestId = generateRequestId()

  async function fetchEventsPollingAuth(revision?: string, attempt = 0): Promise<void> {
    if (attempt >= MAX_POLLING_ATTEMPTS) {
      console.warn(`[fetchSharedRouteEditAuth] polling timeout after ${MAX_POLLING_ATTEMPTS} attempts`)
      return
    }

    const response = await fetchEventsAuth({key, revision})

    const succeeded = response.events.find(
      event => event.type === 'order-edit-succeeded' && 'request_id' in event && event.request_id === requestId,
    ) as t.OrderEditSucceeded | undefined

    const failed = response.events.find(
      event => event.type === 'order-edit-failed' && 'request_id' in event && event.request_id === requestId,
    ) as t.OrderEditFailed | undefined

    if (succeeded) {
      return
    }

    if (failed) {
      throw new Error('Order edit failed')
    }

    return delay(1000).then(() => fetchEventsPollingAuth(response.revision, attempt + 1))
  }

  return fetchEdit(key, requestId, expirationDate)
    .then(() => delay(1000))
    .then(() => fetchEventsPollingAuth())
}
